import { useGetDownloadSignedUrlForQuestionCreationTaskLazyQuery } from '@exam-management/graphql';
import { useCallback } from 'react';

export function parseContentForApi(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  const imgElements = doc.getElementsByTagName('img');

  for (const img of Array.from(imgElements)) {
    // Only process images that have both src and content attributes
    if (img.getAttribute('content') && img.hasAttribute('src')) {
      img.removeAttribute('src');
    }
  }

  // Return the processed HTML string
  return doc.body.innerHTML;
}

// Function to extract all filenames from the HTML content
export function extractImagesFileNames(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  const filenames: string[] = [];

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr) {
      filenames.push(contentAttr);
    }
  }

  return [...new Set(filenames)]; // Remove duplicates
}

// Function to update the src attribute of img tags with the provided URL object
export function updateImageTags(content: string, urlObject: Record<string, string>) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr && urlObject[contentAttr]) {
      img.setAttribute('src', urlObject[contentAttr]);
    }
  }

  return doc.body.innerHTML;
}

interface UseParseContentForQuestionCreationTaskInput {
  taskId: string;
}

export default function useParseContentForQuestionCreationTask(
  params: UseParseContentForQuestionCreationTaskInput,
) {
  const { taskId } = params;
  const [getDownloadSignedUrlForQuestionCreationTask] =
    useGetDownloadSignedUrlForQuestionCreationTaskLazyQuery();

  const parseContentForQuestionCreationTask = useCallback(
    async (content: string) => {
      const fileNames = extractImagesFileNames(content);

      if (fileNames.length === 0) {
        return content;
      }

      const { data } = await getDownloadSignedUrlForQuestionCreationTask({
        variables: {
          input: { fileNames, taskId },
        },
      });

      const urlObject: Record<string, string> | undefined =
        data?.wflGetDownloadSignedUrlForTask?.signedUrls;

      if (!urlObject) {
        return content;
      }

      return updateImageTags(content, urlObject);
    },
    [taskId, getDownloadSignedUrlForQuestionCreationTask],
  );

  return { parseContentForQuestionCreationTask };
}
